// src/features/admin/orders/ResultsUploadPanel.tsx
import React, { useEffect, useMemo, useState } from "react";
import Button from "components/ui/Button";
import Input from "components/ui/Input";
import {
  adminCopyOrderResult,
  adminCreateManualResult,
  adminDeleteOrderResult,
  adminGetOrderResultDetail,
  adminListOrderResults,
  adminUpdateOrderResult,
  adminUploadOrderResult,
  type AdminManualResultIn,
  type AdminOrderResultDetailOut,
  type AdminOrderResultRowOut
} from "api/adminResults";
import { archiveOrderResult, publishOrderResult } from "api/admin";
import { conflictCurrentVersion, getErrorMessage, isVersionConflict } from "utils/errors";
import ResultsTable from "./ResultsTable";
import ResultsDetailDrawer from "./ResultsDetailDrawer";

interface Props {
  orderId: number;
  orderLockVersion: number;
  onOrderChanged?: () => Promise<void>;
}

const GOLD = "#f3dfae";

const ALLOWED_EXTS = [".docx", ".pdf"];
const ALLOWED_MIMES = [
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/pdf"
];

function isAllowedFile(f: File): boolean {
  const name = f.name.toLowerCase();
  const extOk = ALLOWED_EXTS.some((e) => name.endsWith(e));
  const mimeOk = ALLOWED_MIMES.includes(f.type) || f.type === "";
  return extOk && mimeOk;
}

function pickSelectIdFromManualOut(out: any): number | undefined {
  const brandedId = out?.branded_pdf?.id;
  const structuredId = out?.structured?.id;
  if (Number.isFinite(brandedId)) return Number(brandedId);
  if (Number.isFinite(structuredId)) return Number(structuredId);
  return undefined;
}

function isOriginal(r: AdminOrderResultRowOut): boolean {
  return r.file_role_code === "original";
}

function isBranded(r: AdminOrderResultRowOut): boolean {
  return r.file_role_code === "branded";
}

const ResultsUploadPanel: React.FC<Props> = ({ orderId, orderLockVersion, onOrderChanged }) => {
  const [items, setItems] = useState<AdminOrderResultRowOut[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [err, setErr] = useState("");

  // Upload
  const [file, setFile] = useState<File | null>(null);
  const [uploadSummary, setUploadSummary] = useState("");
  const [uploading, setUploading] = useState(false);

  // Selected + modal
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const selectedRow = useMemo(
    () => (selectedId != null ? items.find((r) => r.id === selectedId) ?? null : null),
    [items, selectedId]
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"view" | "create" | "edit">("view");

  const [detail, setDetail] = useState<AdminOrderResultDetailOut | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [copyingId, setCopyingId] = useState<number | null>(null);

  async function refreshList(opts?: { preferSelectId?: number }) {
    setErr("");
    setLoadingList(true);
    try {
      const results = await adminListOrderResults(orderId);
      setItems(results);

      setSelectedId((prev) => {
        const prefer = opts?.preferSelectId;
        if (prefer != null && results.some((r) => r.id === prefer)) return prefer;
        if (prev != null && results.some((r) => r.id === prev)) return prev;
        return results[0]?.id ?? null;
      });
    } catch (e: any) {
      setErr(getErrorMessage(e, "Failed to load results"));
    } finally {
      setLoadingList(false);
    }
  }

  async function loadDetail(resultId: number) {
    setLoadingDetail(true);
    try {
      const d = await adminGetOrderResultDetail(orderId, resultId);
      setDetail(d);
    } catch (e: any) {
      setDetail(null);
      setErr(getErrorMessage(e, "Failed to load result detail"));
    } finally {
      setLoadingDetail(false);
    }
  }

  useEffect(() => {
    void refreshList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  async function handleUpload(e: React.FormEvent) {
    e.preventDefault();
    if (!file) return;

    if (!isAllowedFile(file)) {
      setErr("Unsupported file type. Please upload a .docx or .pdf file.");
      return;
    }

    setErr("");
    setUploading(true);
    try {
      await adminUploadOrderResult(orderId, {
        file,
        summary: uploadSummary.trim() || undefined,
        expected_lock_version: orderLockVersion
      });
      setFile(null);
      setUploadSummary("");
      await refreshList();
    } catch (e: any) {
      setErr(getErrorMessage(e, "Failed to upload result"));
    } finally {
      setUploading(false);
    }
  }

  function openCreate() {
    setSelectedId(null);
    setDetail(null);
    setModalMode("create");
    setModalOpen(true);
  }

  async function openView(r: AdminOrderResultRowOut) {
    setSelectedId(r.id);
    setModalMode("view");
    setModalOpen(true);
    await loadDetail(r.id);
  }

  async function openEdit(r: AdminOrderResultRowOut) {
    if (isOriginal(r)) {
      setErr("Original uploads are immutable. Use Copy to create an editable branded version.");
      return;
    }
    if (!isBranded(r)) {
      setErr("Only branded PDFs are editable. Use Copy to create a branded version.");
      return;
    }

    setSelectedId(r.id);
    setModalMode("edit");
    setModalOpen(true);
    await loadDetail(r.id);
  }

  // Copy must call /copy to create ONE branded row with a PDF
  async function handleCopy(r: AdminOrderResultRowOut) {
    if (copyingId != null) return;
    setErr("");
    setCopyingId(r.id);
    try {
      const copied = await adminCopyOrderResult(orderId, r.id, {
        expected_lock_version: r.lock_version,
        title: r.title ?? null,
        summary: r.summary ?? null
      });

      await refreshList({ preferSelectId: copied.id });
      setSelectedId(copied.id);
      setDetail(copied);
      setModalMode("edit");
      setModalOpen(true);
    } catch (e: any) {
      if (isVersionConflict(e)) {
        const current = conflictCurrentVersion(e);
        setErr(
          current
            ? `Version conflict (current v${current}). Reloaded latest results.`
            : "Version conflict. Reloaded latest results."
        );
        await refreshList();
        return;
      }
      setErr(getErrorMessage(e, "Failed to copy result"));
    } finally {
      setCopyingId(null);
    }
  }

  async function handlePublish(r: AdminOrderResultRowOut) {
    setErr("");
    try {
      await publishOrderResult(orderId, r.id, {
        expected_lock_version: r.lock_version,
        mark_order_resulted: true,
        order_expected_lock_version: orderLockVersion
      });
      await refreshList({ preferSelectId: r.id });
      if (onOrderChanged) await onOrderChanged();
    } catch (e: any) {
      if (isVersionConflict(e)) {
        const current = conflictCurrentVersion(e);
        setErr(
          current
            ? `Version conflict (current v${current}). Reloaded latest results.`
            : "Version conflict. Reloaded latest results."
        );
        await refreshList({ preferSelectId: r.id });
        if (onOrderChanged) await onOrderChanged();
      } else {
        setErr(getErrorMessage(e, "Failed to publish result"));
      }
    }
  }

  async function handleArchive(r: AdminOrderResultRowOut) {
    setErr("");
    try {
      await archiveOrderResult(orderId, r.id, { expected_lock_version: r.lock_version });
      await refreshList({ preferSelectId: r.id });
    } catch (e: any) {
      if (isVersionConflict(e)) {
        const current = conflictCurrentVersion(e);
        setErr(
          current
            ? `Version conflict (current v${current}). Reloaded latest results.`
            : "Version conflict. Reloaded latest results."
        );
        await refreshList({ preferSelectId: r.id });
      } else {
        setErr(getErrorMessage(e, "Failed to archive result"));
      }
    }
  }

  async function handleDelete(r: AdminOrderResultRowOut) {
    if (!window.confirm(`Delete result #${r.id}? This is a hard delete.`)) return;

    setErr("");
    try {
      await adminDeleteOrderResult(orderId, r.id, {
        expected_lock_version: r.lock_version,
        cascade: true,
        delete_files: true
      });

      if (selectedId === r.id) {
        setSelectedId(null);
        setDetail(null);
        setModalOpen(false);
      }

      await refreshList();
    } catch (e: any) {
      if (isVersionConflict(e)) {
        const current = conflictCurrentVersion(e);
        setErr(
          current
            ? `Version conflict (current v${current}). Reloaded latest results.`
            : "Version conflict. Reloaded latest results."
        );
        await refreshList();
        return;
      }
      setErr(getErrorMessage(e, "Failed to delete result"));
    }
  }

  async function handleCreateSubmit(payload: AdminManualResultIn) {
    setErr("");
    const out = await adminCreateManualResult(orderId, payload);
    const selectId = pickSelectIdFromManualOut(out);

    await refreshList({ preferSelectId: selectId });
    if (selectId != null) await loadDetail(selectId);

    setModalMode("view");
    setModalOpen(false);
  }

  async function handleEditSubmit(
    payload: AdminManualResultIn,
    ctx: { row: AdminOrderResultRowOut; detail: AdminOrderResultDetailOut | null }
  ) {
    setErr("");
    try {
      if (ctx.row.file_role_code !== "branded") {
        setErr("Only branded PDFs are editable. Use Copy to create an editable branded version.");
        return;
      }

      const currentLock = ctx.detail?.lock_version ?? ctx.row.lock_version;

      const updated = await adminUpdateOrderResult(orderId, ctx.row.id, {
        expected_lock_version: currentLock,
        title: payload.title ?? null,
        summary: payload.summary ?? null,
        content_json: payload.content_json
      });

      await refreshList({ preferSelectId: updated.id });
      setDetail(updated);
      setModalMode("view");
      setModalOpen(false);
    } catch (e: any) {
      if (isVersionConflict(e)) {
        const current = conflictCurrentVersion(e);
        setErr(
          current
            ? `Version conflict (current v${current}). Reloaded latest detail.`
            : "Version conflict. Reloaded latest detail."
        );
        await refreshList({ preferSelectId: ctx.row.id });
        await loadDetail(ctx.row.id);
        return;
      }

      setErr(getErrorMessage(e, "Failed to save changes"));
      throw e;
    }
  }

  return (
    <section className="rounded-2xl border border-slate-200 p-5 space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-ui font-bold uppercase tracking-wide text-slate-800">Results</h2>
          <div className="mt-1 font-ui text-xs uppercase tracking-wide text-slate-500">
            Upload, manage, and publish result documents.
          </div>
        </div>

        <div className="flex gap-2">
          <Button type="button" onClick={openCreate}>
            New result
          </Button>
          <Button type="button" variant="secondary" onClick={() => refreshList()}>
            Refresh
          </Button>
        </div>
      </div>

      {/* Error */}
      {err && (
        <div className="border border-red-300 bg-red-50 text-red-700 p-3 rounded">
          {err}
        </div>
      )}

      {/* Upload */}
      <section className="rounded-2xl border p-5" style={{ borderColor: `${GOLD}B3` }}>
        <h3 className="font-ui font-bold uppercase tracking-wide text-slate-800">
          Upload docx / pdf
        </h3>

        <form onSubmit={handleUpload} className="mt-4 space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <input
              type="file"
              accept=".docx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              disabled={uploading}
              className="text-sm border rounded-xl p-2.5 flex-1 w-full"
            />

            <Button type="submit" disabled={uploading || !file}>
              {uploading ? "Uploading…" : "Upload"}
            </Button>
          </div>

          <Input
            id="upload-summary"
            label="Summary (optional)"
            value={uploadSummary}
            onChange={(e) => setUploadSummary(e.target.value)}
            placeholder="Short summary shown in admin list"
          />
        </form>

        <div className="mt-3 font-ui text-xs uppercase tracking-wide text-slate-500">
          Allowed: .docx, .pdf
        </div>
      </section>

      {/* Table */}
      {loadingList ? (
        <div className="font-body text-sm text-slate-600 italic">Loading results…</div>
      ) : (
        <ResultsTable
          results={items}
          selectedId={selectedId}
          onView={(r) => void openView(r)}
          onEdit={(r) => void openEdit(r)}
          onCopy={(r) => void handleCopy(r)}
          onPublish={(r) => void handlePublish(r)}
          onArchive={(r) => void handleArchive(r)}
          onDelete={(r) => void handleDelete(r)}
        />
      )}

      {/* Drawer */}
      <ResultsDetailDrawer
        open={modalOpen}
        mode={modalMode}
        row={selectedRow}
        detail={detail}
        loadingDetail={loadingDetail}
        onClose={() => {
          setModalOpen(false);
          setModalMode("view");
        }}
        onRender={() => {
          /* intentionally not exposed */
        }}
        onCreateSubmit={handleCreateSubmit}
        onEditSubmit={handleEditSubmit}
      />
    </section>
  );
};

export default ResultsUploadPanel;
